import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTimes,
  faInfoCircle,
  faToggleOn,
  faDollarSign,
  faPalette,
  faEnvelope,
  faWrench,
  faUsers,
  faEye,
  faEyeSlash,
  faList,
  faTags,
} from "@fortawesome/free-solid-svg-icons";
import { useTheme, useUser } from "ogcommon";
import UsersTab from "./UsersTab";
import ScheduledReportsPanel from "./ScheduledReports"; // ← NEW
import TagsTab from "./TagsTab"; // ← NEW (same dir)
import DatabaseManager from "./DatabaseManager";
import LeasesTab from "./LeasesTab";
import PurchasersTab from "./PurchasersTab";

/* ══════════════════════════ component ══════════════════════════ */

// Optionally import axios if your "LeasesTab" needs it:
// import axios from "axios";

/**
 * This component now includes a "Leases" tab which displays and edits
 * the `leases` array returned by GET ?company=... from clientDetails.php.
 *
 * On save/patch, we send:
 * {
 *   ...formData,
 *   users,  // array
 *   leases, // array
 * }
 *
 * The server upserts them based on "LeaseID".
 */

// Utility to standardize input styles
const getInputClasses = (theme, { readOnly = false } = {}) => {
  return `
    w-full px-3 py-2 rounded outline-none border
    transition-colors duration-300
    ${readOnly ? "opacity-70 cursor-not-allowed" : ""}
    ${
      theme === "dark"
        ? "bg-gray-700 text-gray-100 border-gray-600 focus:ring-2 focus:ring-blue-600"
        : "bg-white text-gray-800 border-gray-300 focus:ring-2 focus:ring-blue-500"
    }
  `;
};

function attachLogsToClients(rawClients, rawLogs) {
  if (!Array.isArray(rawClients) || !Array.isArray(rawLogs)) {
    return []; // safest fallback
  }

  // 1️⃣  regroup logs by subdomain once (O(n))
  const logsBySub = rawLogs.reduce((tbl, log) => {
    const sub = (log.Subdomain || "").toLowerCase().trim();
    (tbl[sub] ??= []).push(log);
    return tbl;
  }, {});

  // 2️⃣  clone each client and tack on its logs (or [])
  return rawClients.map((cl) => ({
    ...cl,
    logs: logsBySub[(cl.name || "").toLowerCase().trim()] ?? [],
  }));
}

// ═══════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════
const CompanyDashboard = () => {
  const { theme } = useTheme();
  const CompanyLogs = React.lazy(() => import("./CompanyLogs"));

  /* master list straight from API */
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showActive, setShowActive] = useState(true);
  const { userRole, userID, setUser, companyName } = useUser();
  const [jobTypes, setJobTypes] = useState([]);

  // Gate for the Database Manager panel.
  // Exclusively available to the single super-access user whose UserID is
  // exactly "admin".  Do NOT use role as the primary gate.
  const isPrivilegedAdmin = userID === "admin";

  // DB Manager modal state — only used when isPrivilegedAdmin
  const [showDbModal,        setShowDbModal]        = useState(false);
  const [dbModalSubdomain,   setDbModalSubdomain]   = useState(null);
  const [dbModalInitialTab,  setDbModalInitialTab]  = useState("fieldticket");

  const openDbModal = (subdomain, tab) => {
    setDbModalSubdomain(subdomain || null);
    setDbModalInitialTab(tab || "fieldticket");
    setShowDbModal(true);
  };

  /*───────────────────────────────────────────────────────────────
   * 🔄 Auto-expand & smooth-scroll for newly added wells/tanks
   *   – place these inside your component (above the return).
   *──────────────────────────────────────────────────────────────*/

  /*───────────────────────────────────────────────────────────────
 * Wells & Tanks section – regenerated 2025-07-23
 *──────────────────────────────────────────────────────────────
  /* big modal state – unchanged from your original code */
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [formData, setFormData] = useState(() => defaultFormData());
  const [users, setUsers] = useState([]);
  const [leases, setLeases] = useState([]);
  const [purchasers, setPurchasers] = useState([]);
  const [tags, setTags] = useState([]);
  const [activeTab, setActiveTab] = useState("basic");
  const [auditLog, setAuditLog] = useState([]);
  const [useFieldTicket, setUseFieldTicket] = useState("");

  // Snapshot of the freshly-loaded server state taken when the edit modal opens.
  // Used by handleUpdate to diff and only PATCH the sections that actually changed,
  // avoiding redundant bulk-upserts of stale/legacy rows (which can trigger FK errors).
  const initialStateRef = useRef(null);

  /* ──────────────────────────────────────────────────────────────────
     FETCH all companies (+ their nested logs) once on mount
     ────────────────────────────────────────────────────────────────── */
  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(
        "https://house.ogendeavors.com/api/clientDetails.php",
      );
      const js = await r.json();
      setCompanies(js); // raw – with their own logs & audits arrays
    } catch (err) {
      console.error("fetchCompanies:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();

    const saved = localStorage.getItem("showActive");
    if (saved !== null) setShowActive(JSON.parse(saved));
  }, [fetchCompanies]);

  /* 
     openEditForm   (called when a table row is clicked)
     ──────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────────── */
  // openEditForm — hydrate formData, sync perms.UseFieldTicket and top-level UseFieldTicket
  const openEditForm = useCallback(async (row) => {
    setFormMode("edit");
    setLoading(true);

    try {
      const resp = await fetch(
        `https://house.ogendeavors.com/api/clientDetails.php?company=${encodeURIComponent(
          row?.subdomain || "",
        )}`,
      );
      const data = await resp.json();
      if (!Array.isArray(data) || !data.length) {
        throw new Error(
          `No data returned for “${row?.company || row?.subdomain}”`,
        );
      }
      const single = data[0];

      setAuditLog(Array.isArray(single.auditLog) ? single.auditLog : []);

      // start from defaults, overlay server values
      const fresh = { ...defaultFormData() };
      Object.keys(fresh).forEach((k) => {
        if (single[k] !== undefined) fresh[k] = single[k];
      });

      // parse perms
      let perms = {};
      if (typeof single.perms === "string") {
        try {
          perms = JSON.parse(single.perms);
        } catch {
          perms = {};
        }
      } else if (single.perms && typeof single.perms === "object") {
        perms = single.perms;
      }

      // UseFieldTicket source-of-truth: the top-level DB column returned by
      // clientDetails.php (set to 'Y' by the FT init console on success).
      // The perms JSON may also contain a UseFieldTicket toggle, but the DB
      // column is authoritative for "has the module been initialized?".
      const dbColUft = (single.UseFieldTicket === "Y" || single.UseFieldTicket === "y") ? "Y" : "N";
      const permsUft =
        perms && Object.prototype.hasOwnProperty.call(perms, "UseFieldTicket")
          ? perms.UseFieldTicket === true || perms.UseFieldTicket === "Y"
            ? "Y"
            : "N"
          : dbColUft;
      // If the DB column says Y (init succeeded), that wins regardless of perms
      const uft = dbColUft === "Y" ? "Y" : permsUft;

      // ensure perms object reflects Y/N for UseFieldTicket
      fresh.perms = { ...perms, UseFieldTicket: uft };
      // keep legacy top-level in sync — this drives the "Init: Cleared" badge
      fresh.UseFieldTicket = uft;

      // PRFiler can come back as null (no row) — normalize to an empty 4-field
      // object so the Regulatory tab inputs are always controlled. Server-loaded
      // values override the empty defaults.
      fresh.prFiler = single.prFiler && typeof single.prFiler === "object"
        ? { AgentID: "", AgentName: "", AgentEmail: "", AgentPhone: "", ...single.prFiler }
        : { AgentID: "", AgentName: "", AgentEmail: "", AgentPhone: "" };

      setFormData(fresh);
      setUseFieldTicket(uft);

      const freshUsers  = Array.isArray(single.users)  ? single.users  : [];
      const freshLeases = Array.isArray(single.leases) ? single.leases : [];
      const freshTags   = Array.isArray(single.tags)   ? single.tags   : [];

      setJobTypes(Array.isArray(single.jobTypes) ? single.jobTypes : []);
      setUsers(freshUsers);
      setLeases(freshLeases);
      setTags(freshTags);
      setPurchasers(Array.isArray(single.purchasers) ? single.purchasers : []);

      // Snapshot the server-loaded state so handleUpdate can diff against it
      // and only PATCH the sections that actually changed.
      initialStateRef.current = {
        formData: JSON.parse(JSON.stringify(fresh)),
        users:    JSON.parse(JSON.stringify(freshUsers)),
        leases:   JSON.parse(JSON.stringify(freshLeases)),
        tags:     JSON.parse(JSON.stringify(freshTags)),
      };

      setActiveTab("basic");
      setShowForm(true);
    } catch (err) {
      console.error("openEditForm:", err);
      alert("Could not open client – see console for details.");
    } finally {
      setLoading(false);
    }
  }, []);

  // --------------------------------------------------------------------

  /* ──────────────────────────────────────────────────────────────
   openEditForm  –  launches the big “Edit Company” modal
   • row           – table row that was clicked; must contain .company
   • always calls the live API for the freshest data
   ──────────────────────────────────────────────────────────────*/
  // default form fields
  // --------------------------------------------------------------------
  function defaultFormData() {
    return {
      subdomain: "",
      name: "",
      user: "",
      pwd: "",
      company: "",
      useBSW: "N",
      use24: "N",
      useMaps: "N",
      useLinePressure: "N",
      ShowWells: "N",
      Active: "Y",
      Notes: "",
      UseWater: "Y",
      UseGas: "Y",
      Type: "O",
      ShowWellPropertyNum: "N",
      OperatorNum: "",
      PumperEditDaysBack: 0,
      UseWaterTanks: "N",
      ShowZeros: "N",
      UseColorCut: "N",
      AppName: "ogPumper",
      AppLogo: "http://ogPumper.com/images/logo.jpg",
      AppAuthor: "ogEndeavors, LLC",
      SupportEmail: "support@ogPumper.com",
      SupportPhone: "325-261-0394",
      SupportAddress: "ogEndeavors, LLC, PO Box 7091, Abilene, TX 79608",
      Bill: "Y",
      Rate1: 5.0,
      Rate2: 5.0,
      Rate3: 5.0,
      GaugeImportFile: "N",
      NetInterestBbls: "N",
      PR: "N",
      ShowPOC: "N",
      CurrentCustomCol: "LeaseID",
      CurrentCustomCol2: "PumperID",
      ShowLoads: "N",
      BillQuarterly: "N",
      ManualWaterLabel: "Water Bbls Gauged",
      ManualWaterLabel2: "*Enter bbls if not entering gauges",
      Data: "N",
      RequireWellReason: "N",
      UseImages: "N",
      PRStartDay: 2,
      CurrentLayout: "O",
      AlwaysOverrideRT: "N",
      ColorCutLabel: "Color Cut Gauge",
      InjCustomCol1: "LeaseID",
      InjCustomCol2: "PumperID",
      NotifyEmails: "",
      lineheightinches: "",
      UseFieldTicket: "N",
      UseBradenheadPressure: "N",
      EnableH10SelfInit: "N",
      // Tenant-DB single-row PRFilers (managed in the Regulatory tab).
      prFiler: { AgentID: "", AgentName: "", AgentEmail: "", AgentPhone: "" },
      perms: JSON.stringify({ hasOgFiles: false, UseFieldTicket: false }),
    };
  }

  // --------------------------------------------------------------------
  // close the main form
  // --------------------------------------------------------------------
  function closeForm() {
    setShowForm(false);
    setFormMode("create");
    setFormData(defaultFormData());
    setUsers([]);
    setLeases([]);
    setActiveTab("basic");
    initialStateRef.current = null;
  }

  // --------------------------------------------------------------------
  // Create (POST)
  // --------------------------------------------------------------------
  // --------------------------------------------------------------------
  // Create (POST) – keeps DB creds for true tenant creation flows
  // Stringifies perms if it's an object
  // --------------------------------------------------------------------
  const handleCreate = useCallback(async () => {
    try {
      const payload = { ...formData, users, leases };
      if (payload && typeof payload.perms === "object") {
        payload.perms = JSON.stringify(payload.perms);
      }

      const response = await fetch(
        "https://house.ogendeavors.com/api/clientDetails.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        const msg = await response.text().catch(() => response.statusText);
        throw new Error(`Create failed: ${response.status} ${msg}`);
      }

      await response.json().catch(() => null); // ignore body if not needed
      fetchCompanies();
      closeForm();
    } catch (err) {
      console.error(err);
      alert(err.message || "Create failed. See console for details.");
    }
  }, [formData, users, leases, fetchCompanies]);

  // --------------------------------------------------------------------
  // Update (PATCH) – strictly client-level, diff-aware:
  //   • Strips tenant DB fields (name/user/pwd)
  //   • Compares current state to the snapshot taken in openEditForm
  //   • Only includes sections (company fields / users / leases / tags)
  //     that actually changed — avoids redundant bulk upserts of legacy
  //     rows, which previously crashed on stale FK values (e.g. legacy
  //     WaterTransport='tanner' not present in purchasers).
  //   • For leases, sends only the rows that changed (diff by LeaseID).
  //   • No-op Save shows a friendly message instead of PATCHing.
  // --------------------------------------------------------------------
  const handleUpdate = useCallback(async () => {
    try {
      const snap = initialStateRef.current || {
        formData: {},
        users: [],
        leases: [],
        tags: [],
      };

      // Normalize for diffing: ensure perms is an object on both sides so a
      // no-op save doesn't show a spurious string-vs-object diff.
      const normForCompare = (fd) => {
        const out = { ...(fd || {}) };
        if (typeof out.perms === "string") {
          try { out.perms = JSON.parse(out.perms); } catch { /* leave */ }
        }
        // Drop tenant DB creds so edit-mode (where they're readOnly) never flags dirty.
        delete out.name;
        delete out.user;
        delete out.pwd;
        return out;
      };

      const companyChanged =
        JSON.stringify(normForCompare(formData)) !==
        JSON.stringify(normForCompare(snap.formData));

      const usersChanged =
        JSON.stringify(users || []) !== JSON.stringify(snap.users || []);

      const tagsChanged =
        JSON.stringify(tags || []) !== JSON.stringify(snap.tags || []);

      // Lease diff: only send rows that are new or changed since the modal opened.
      const snapLeasesById = new Map(
        (snap.leases || []).map((l) => [l.LeaseID, JSON.stringify(l)]),
      );
      const changedLeases = (leases || []).filter((l) => {
        const prev = snapLeasesById.get(l.LeaseID);
        return prev === undefined || prev !== JSON.stringify(l);
      });
      const leasesChanged = changedLeases.length > 0;

      if (!companyChanged && !usersChanged && !tagsChanged && !leasesChanged) {
        alert("No changes to save.");
        closeForm();
        return;
      }

      const payload = {};

      if (companyChanged) {
        const { name, user, pwd, ...clientOnly } = formData; // drop tenant creds
        Object.assign(payload, clientOnly);
        if (typeof payload.perms === "object") {
          payload.perms = JSON.stringify(payload.perms);
        }
      }

      if (usersChanged)  payload.users  = users;
      if (leasesChanged) payload.leases = changedLeases;
      if (tagsChanged)   payload.tags   = tags;

      const url = `https://house.ogendeavors.com/api/clientDetails.php?company=${encodeURIComponent(
        formData.subdomain,
      )}`;

      const response = await fetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const msg = await response.text().catch(() => response.statusText);
        throw new Error(`Update failed: ${response.status} ${msg}`);
      }

      await response.json().catch(() => null); // ignore body if not needed
      fetchCompanies();
      closeForm();
    } catch (err) {
      console.error(err);
      alert(err.message || "Update failed. See console for details.");
    }
  }, [formData, users, leases, tags, fetchCompanies]);

  // --------------------------------------------------------------------
  // Handle "Submit" in the main modal
  // --------------------------------------------------------------------
  const handleSubmitForm = () => {
    if (formMode === "create") {
      handleCreate();
    } else {
      handleUpdate();
    }
  };

  // --------------------------------------------------------------------
  // Toggle "active" vs "inactive" in top bar
  // --------------------------------------------------------------------
  const handleToggleActive = useCallback(() => {
    setShowActive((prev) => {
      const newVal = !prev;
      localStorage.setItem("showActive", JSON.stringify(newVal));
      return newVal;
    });
  }, []);

  // --------------------------------------------------------------------
  // Search + Active filtering
  // --------------------------------------------------------------------
  const filteredCompanies = useMemo(() => {
    const filteredByActive = showActive
      ? companies.filter((co) => co.Active === "Y")
      : companies.filter((co) => co.Active !== "Y");

    if (!searchQuery) return filteredByActive;

    const lower = searchQuery.toLowerCase();
    return filteredByActive.filter(
      (c) =>
        (c.company || "").toLowerCase().includes(lower) ||
        (c.subdomain || "").toLowerCase().includes(lower),
    );
  }, [companies, showActive, searchQuery]);

  // --------------------------------------------------------------------
  // Generic field change handler (supports object passthrough for `perms`)
  // --------------------------------------------------------------------
  const handleFieldChange = (e) => {
    const { name, value } = e.target ?? {};
    // If a child passes an object (e.g., BasicInfoTab sends perms as object),
    // accept it as-is. Otherwise use the event value.
    const next = name === "perms" && typeof value === "object" ? value : value;
    setFormData((prev) => ({ ...prev, [name]: next }));
  };

  // Nested-object setter for the PR Filer block (Regulatory tab). Writes into
  // formData.prFiler.<field>; the diff-aware handleUpdate then picks it up and
  // the server's PATCH handler upserts the tenant PRFilers row.
  const handlePrFilerChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      prFiler: { ...(prev.prFiler || {}), [name]: value },
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: checked ? "Y" : "N" };

      if (name === "UseFieldTicket") {
        const p = normaliseClientPerms(prev.perms);
        next.perms = { ...p, UseFieldTicket: checked }; // boolean
      }
      return next;
    });
  };

  const useFieldTicketBool = useMemo(
    () => normaliseClientPerms(formData.perms).UseFieldTicket,
    [formData.perms],
  );

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${
        theme === "dark"
          ? "bg-gray-900 text-gray-100"
          : "bg-gray-100 text-gray-900"
      }`}
    >
      <div className="container mx-auto px-4 py-8">
        {/* Title + Search + Active/Inactive Toggle */}
        <div className="w-full max-w-4xl mx-auto mb-6">
          <div
            className={`p-6 text-center rounded-xl shadow-md relative ${
              theme === "dark"
                ? "bg-gradient-to-r from-gray-800 to-gray-700"
                : "bg-gradient-to-r from-blue-500 to-indigo-500"
            }`}
          >
            <h2 className="text-3xl font-bold mb-4 text-white">
              Company Dashboard
            </h2>
            <div className="absolute top-4 right-4">
              <button
                onClick={handleToggleActive}
                className="inline-flex items-center justify-center font-bold py-2 px-4 rounded-full shadow-lg transition-transform duration-200 ease-in-out hover:scale-105 bg-white hover:bg-gray-200 text-blue-600"
              >
                <FontAwesomeIcon
                  icon={showActive ? faEye : faEyeSlash}
                  className="mr-2"
                />
                {showActive ? "Active" : "Inactive"}
              </button>
            </div>
            <div className="mb-6 flex flex-col sm:flex-row items-center sm:justify-center gap-4 mt-4">
              {/* Search input */}
              <input
                type="text"
                placeholder="Search by company name..."
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`
                  w-full max-w-md px-4 py-2 rounded-lg shadow-md focus:outline-none
                  focus:ring-2 transition-all duration-300
                  ${
                    theme === "dark"
                      ? "bg-gray-700 text-gray-100 placeholder-gray-400 border-gray-600 focus:ring-blue-600"
                      : "bg-white text-gray-800 placeholder-gray-500 border-gray-300 focus:ring-blue-500"
                  }
                `}
              />
              {/* Create New Company — admin only, opens provisioning modal */}
              {isPrivilegedAdmin && (
                <button
                  onClick={() => openDbModal(null, "create")}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-md bg-green-600 hover:bg-green-700 text-white transition-colors duration-200 whitespace-nowrap"
                >
                  <FontAwesomeIcon icon={faPlus} />
                  New Company
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Companies list */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredCompanies.length === 0 ? (
          <p className="text-center text-gray-500 dark:text-gray-300">
            No matches found.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCompanies.map((co) => (
              <div
                key={co.subdomain}
                className={`flex flex-col justify-between p-4 rounded-lg shadow-md border transform transition-all duration-200 hover:scale-105 ${
                  theme === "dark"
                    ? "bg-gray-800 hover:bg-gray-700 text-gray-200 border-gray-700"
                    : "bg-white hover:bg-gray-100 text-gray-800 border-gray-200"
                }`}
              >
                <div>
                  <h3 className="font-bold text-xl mb-1">
                    {co.company || "Unknown Company"}
                  </h3>
                  <p
                    className={`text-sm ${theme === "dark" ? "text-gray-300" : "text-gray-500"}`}
                  >
                    Subdomain: {co.subdomain}
                  </p>
                  <p className="text-sm mt-1">
                    Status:{" "}
                    <span
                      className={`${co.Active === "Y" ? "text-green-600" : "text-red-600"}`}
                    >
                      {co.Active === "Y" ? "Active" : "Inactive"}
                    </span>
                  </p>
                </div>
                <div className="mt-3 flex space-x-2">
                  {/* EDIT button -> triggers openEditForm */}
                  <button
                    onClick={() => openEditForm(co)}
                    className="flex-1 rounded-full py-2 font-semibold text-center bg-blue-500 hover:bg-blue-600 text-white"
                  >
                    <FontAwesomeIcon icon={faEdit} className="mr-2" />
                    Edit
                  </button>
                  {/* DB Tools button — admin only */}
                  {isPrivilegedAdmin && (
                    <button
                      onClick={() => openDbModal(co.subdomain, "fieldticket")}
                      title="Database Tools"
                      className={`rounded-full py-2 px-3 font-semibold text-center transition-colors duration-200 ${
                        theme === "dark"
                          ? "bg-gray-700 hover:bg-gray-600 text-gray-200"
                          : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                      }`}
                    >
                      <FontAwesomeIcon icon={faWrench} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Database Manager modal — per-tenant, admin only */}
      {showDbModal && isPrivilegedAdmin && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-60 p-4">
          <div
            className={`w-full max-w-4xl rounded-xl shadow-2xl overflow-y-auto max-h-[90vh] relative ${
              theme === "dark" ? "bg-gray-800" : "bg-white"
            }`}
          >
            {/* Modal close button */}
            <button
              onClick={() => setShowDbModal(false)}
              className={`absolute top-3 right-3 z-10 p-1.5 rounded-full transition-colors ${
                theme === "dark"
                  ? "text-gray-400 hover:text-gray-100 hover:bg-gray-700"
                  : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
              }`}
              title="Close"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <DatabaseManager
              tenantSubdomain={dbModalSubdomain}
              initialTab={dbModalInitialTab}
            />
          </div>
        </div>
      )}

      {/* Main Modal for create/edit a single company */}
      {showForm && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div
            className={`w-full max-w-5xl mx-auto p-6 rounded-lg shadow-2xl relative transition-colors duration-300 ${
              theme === "dark" ? "bg-gray-800 text-gray-100" : "bg-white"
            }`}
          >
            {/* Header row */}
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center space-x-4">
                <h2 className="text-2xl font-bold">
                  {formMode === "create"
                    ? "Create New Company"
                    : "Edit Company"}
                </h2>
              </div>
              {formMode === "edit" && (() => {
                /* Tenant domain: .net by default, .com exception for sojo */
                const tld = (formData.subdomain || "").toLowerCase() === "sojo" ? "com" : "net";
                const host = `${formData.subdomain}.${formData.AppName}.${tld}`;
                return (
                  <a
                    href={`https://${host}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-block font-semibold text-lg px-3 py-1 rounded ${
                      theme === "dark"
                        ? "bg-gray-700 text-gray-100"
                        : "bg-gray-200 text-gray-800"
                    } hover:underline`}
                  >
                    {host}
                  </a>
                );
              })()}

              <button
                onClick={closeForm}
                className="p-2 rounded-full text-gray-600 hover:text-gray-900 dark:hover:text-gray-200"
              >
                <FontAwesomeIcon icon={faTimes} size="lg" />
              </button>
            </div>

            {/* Tab bar */}
            <div className="mb-4 border-b border-gray-300 dark:border-gray-600 flex flex-wrap gap-2">
              <TabButton
                label="Basic Info"
                icon={faInfoCircle}
                active={activeTab === "basic"}
                onClick={() => setActiveTab("basic")}
              />
              <TabButton
                label="Features"
                icon={faToggleOn}
                active={activeTab === "features"}
                onClick={() => setActiveTab("features")}
              />
              <TabButton
                label="Regulatory"
                icon={faInfoCircle}
                active={activeTab === "regulatory"}
                onClick={() => setActiveTab("regulatory")}
              />
              <TabButton
                label="Billing"
                icon={faDollarSign}
                active={activeTab === "billing"}
                onClick={() => setActiveTab("billing")}
              />
              <TabButton
                label="Layout/Labels"
                icon={faPalette}
                active={activeTab === "layout"}
                onClick={() => setActiveTab("layout")}
              />
              <TabButton
                label="Support"
                icon={faEnvelope}
                active={activeTab === "support"}
                onClick={() => setActiveTab("support")}
              />
              <TabButton
                label="Misc"
                icon={faWrench}
                active={activeTab === "misc"}
                onClick={() => setActiveTab("misc")}
              />
              <TabButton
                label="Users"
                icon={faUsers}
                active={activeTab === "users"}
                onClick={() => setActiveTab("users")}
              />
              <TabButton
                label="Leases"
                icon={faList}
                active={activeTab === "leases"}
                onClick={() => setActiveTab("leases")}
              />
              <TabButton
                label="Purchasers"
                icon={faList}
                active={activeTab === "purchasers"}
                onClick={() => setActiveTab("purchasers")}
              />
              <TabButton
                label="Tags"
                icon={faTags}
                active={activeTab === "tags"}
                onClick={() => setActiveTab("tags")}
              />
              <TabButton
                label="Logs"
                icon={faList}
                active={activeTab === "logs"}
                onClick={() => setActiveTab("logs")}
              />
              <TabButton
                label="Scheduled Reports"
                icon={faList}
                active={activeTab === "schedules"}
                onClick={() => setActiveTab("schedules")}
              />
            </div>

            {/* Tab content */}
            <div
              className={`max-h-[60vh] overflow-y-auto p-2 rounded border ${
                theme === "dark" ? "border-gray-600" : "border-gray-200"
              }`}
            >
              {activeTab === "basic" && (
                <BasicInfoTab
                  formData={formData}
                  onChange={handleFieldChange}
                  onToggle={handleCheckboxChange}
                  theme={theme}
                  formMode={formMode}
                />
              )}
              {activeTab === "features" && (
                <FeaturesTab
                  formData={formData}
                  onToggle={handleCheckboxChange}
                  onChange={handleFieldChange}
                  theme={theme}
                />
              )}
              {activeTab === "regulatory" && (
                <RegulatoryTab
                  formData={formData}
                  onChange={handleFieldChange}
                  onToggle={handleCheckboxChange}
                  onPrFilerChange={handlePrFilerChange}
                  theme={theme}
                  subdomain={formData.subdomain}
                />
              )}
              {activeTab === "billing" && (
                <BillingTab
                  formData={formData}
                  onChange={handleFieldChange}
                  onToggle={handleCheckboxChange}
                  theme={theme}
                />
              )}
              {activeTab === "layout" && (
                <LayoutTab
                  formData={formData}
                  onChange={handleFieldChange}
                  theme={theme}
                />
              )}
              {activeTab === "support" && (
                <SupportTab
                  formData={formData}
                  onChange={handleFieldChange}
                  theme={theme}
                />
              )}
              {activeTab === "misc" && (
                <MiscTab
                  formData={formData}
                  onChange={handleFieldChange}
                  onToggle={handleCheckboxChange}
                  theme={theme}
                />
              )}
              {activeTab === "users" && (
                <UsersTab
                  users={users}
                  setUsers={setUsers}
                  theme={theme}
                  companyName={formData.subdomain}
                  role={userRole.role}
                  useFieldTicket={useFieldTicket}
                  jobTypes={jobTypes}
                />
              )}
              {activeTab === "leases" && (
                <LeasesTab
                  leases={leases}
                  setLeases={setLeases}
                  users={users}
                  purchasers={purchasers}
                  tags={tags}
                  theme={theme}
                  companyName={formData.subdomain}
                />
              )}
              {activeTab === "purchasers" && (
                <PurchasersTab
                  purchasers={purchasers}
                  setPurchasers={setPurchasers}
                  theme={theme}
                  companyName={formData.subdomain}
                  baseUrl="https://house.ogendeavors.com"
                />
              )}
              {activeTab === "tags" && (
                <TagsTab
                  tags={tags}
                  setTags={setTags}
                  theme={theme}
                  companyName={formData.subdomain}
                />
              )}
              {activeTab === "logs" && (
                <React.Suspense
                  fallback={
                    <div className="flex h-64 items-center justify-center">
                      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500 border-solid"></div>
                    </div>
                  }
                >
                  <CompanyLogs
                    theme={theme}
                    company={formData.subdomain}
                    apiBase="https://house.ogendeavors.com/api"
                    pageSize={10000}
                    auditSince=""
                    auditSlim={false}
                  />
                </React.Suspense>
              )}
              {activeTab === "schedules" && (
                <ScheduledReportsPanel
                  subdomain={formData.subdomain}
                  users={users}
                  leases={leases}
                  tags={tags}
                  theme={theme}
                  baseUrl="https://house.ogendeavors.com/api"
                />
              )}
            </div>

            {/* Footer */}
            <div className="mt-4 flex justify-end space-x-2">
              <button
                onClick={closeForm}
                className={`px-4 py-2 rounded ${
                  theme === "dark"
                    ? "bg-gray-700 hover:bg-gray-600 text-gray-200"
                    : "bg-gray-300 hover:bg-gray-400 text-gray-800"
                }`}
              >
                Close
              </button>
              <button
                onClick={handleSubmitForm}
                className="px-4 py-2 rounded bg-blue-500 hover:bg-blue-600 text-white"
              >
                {formMode === "create" ? "Create" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/** -------------------------------------
 *  Reusable Tab Button
 * ------------------------------------- */
function TabButton({ label, icon, active, onClick }) {
  return (
    <button
      className={`px-4 py-2 font-semibold rounded-t-lg transition-colors ${
        active
          ? "border-b-2 border-blue-500 text-blue-700"
          : "text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
      }`}
      onClick={onClick}
    >
      <FontAwesomeIcon icon={icon} className="mr-2" />
      {label}
    </button>
  );
}

/*──────────────────────────────────────────────────────────────
 * BasicInfoTab — regenerated 2025-07-31
 * • Checkbox state now honours truthy strings/numbers (“1”, "true", "Y")
 * • `normaliseClientPerms` coerces everything to real booleans
 *──────────────────────────────────────────────────────────────*/

/* ───────────── helpers ───────────── */
const DEFAULT_CLIENT_PERMS = {
  hasOgFiles: false,
  UseFieldTicket: false,
};

const toBool = (v) =>
  v === true ||
  v === 1 ||
  v === "1" ||
  v === "true" ||
  v === "TRUE" ||
  v === "Y" ||
  v === "y";

/** Accepts string | object | undefined, always returns proper boolean flags */
const normaliseClientPerms = (p) => {
  let obj;
  if (!p) obj = {};
  else if (typeof p === "string") {
    try {
      obj = JSON.parse(p);
    } catch {
      obj = {};
    }
  } else obj = { ...p };

  return {
    hasOgFiles: toBool(obj.hasOgFiles ?? DEFAULT_CLIENT_PERMS.hasOgFiles),
    UseFieldTicket: toBool(
      obj.UseFieldTicket ?? DEFAULT_CLIENT_PERMS.UseFieldTicket,
    ),
    hasOgCharts: toBool(obj.hasOgCharts ?? DEFAULT_CLIENT_PERMS.hasOgCharts),
  };
};

function BasicInfoTab({ formData, onChange, onToggle, theme, formMode }) {
  const isReadOnly = formMode === "edit"; // lock db creds on edit
  const perms = normaliseClientPerms(formData.perms);

  /* Update a single flag inside perms */
  const handlePermToggle = (key, checked) => {
    const next = { ...perms, [key]: checked };
    // Pass as *object*; parent can JSON-stringify before saving
    onChange({ target: { name: "perms", value: next } });
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Subdomain */}
      <div>
        <label className="block mb-1 font-semibold">Subdomain</label>
        <input
          name="subdomain"
          value={formData.subdomain}
          onChange={onChange}
          readOnly={isReadOnly}
          className={getInputClasses(theme, { readOnly: isReadOnly })}
        />
      </div>

      {/* DB Name */}
      <div>
        <label className="block mb-1 font-semibold">DB Name</label>
        <input
          name="name"
          value={formData.name}
          onChange={onChange}
          readOnly={isReadOnly}
          className={getInputClasses(theme, { readOnly: isReadOnly })}
        />
      </div>

      {/* DB User */}
      <div>
        <label className="block mb-1 font-semibold">DB User</label>
        <input
          name="user"
          value={formData.user}
          onChange={onChange}
          readOnly={isReadOnly}
          className={getInputClasses(theme, { readOnly: isReadOnly })}
        />
      </div>

      {/* DB Password */}
      <div>
        <label className="block mb-1 font-semibold">DB Password</label>
        <input
          type="password"
          name="pwd"
          value={formData.pwd}
          onChange={onChange}
          readOnly={isReadOnly}
          className={getInputClasses(theme, { readOnly: isReadOnly })}
        />
      </div>

      {/* Company Name */}
      <div>
        <label className="block mb-1 font-semibold">Company Name</label>
        <input
          name="company"
          value={formData.company}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>

      {/* Type (O/P) */}
      <div>
        <label className="block mb-1 font-semibold">
          Type (O=Operator, P=Purchaser)
        </label>
        <select
          name="Type"
          value={formData.Type}
          onChange={onChange}
          className={getInputClasses(theme)}
        >
          <option value="O">Operator</option>
          <option value="P">Purchaser</option>
        </select>
      </div>

      {/* Active toggle */}
      <div className="flex items-center space-x-2 mt-2">
        <input
          id="activeToggle"
          type="checkbox"
          name="Active"
          checked={formData.Active === "Y"}
          onChange={onToggle}
        />
        <label htmlFor="activeToggle" className="font-semibold">
          Active?
        </label>
      </div>

      {/* Notes */}
      <div className="md:col-span-2">
        <label className="block mb-1 font-semibold">Notes</label>
        <textarea
          name="Notes"
          value={formData.Notes}
          onChange={onChange}
          rows={4}
          className={getInputClasses(theme)}
        />
      </div>

      {/* ───── Client-level Permissions ───── */}
      <div className="md:col-span-2 border-t pt-2 mt-2">
        <h5 className="text-sm font-bold mb-1">Permissions</h5>

        {/* hasOgFiles */}
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold">OgFiles enabled</span>
          <label className="flex items-center text-xs">
            <input
              type="checkbox"
              checked={perms.hasOgFiles}
              onChange={(e) => handlePermToggle("hasOgFiles", e.target.checked)}
              className="mr-1"
            />
            Allow
          </label>
        </div>

        {/* hasOgFiles */}
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold">OgCharts enabled</span>
          <label className="flex items-center text-xs">
            <input
              type="checkbox"
              checked={perms.hasOgCharts}
              onChange={(e) =>
                handlePermToggle("hasOgCharts", e.target.checked)
              }
              className="mr-1"
            />
            Allow
          </label>
        </div>

        {/* UseFieldTicket */}
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold">Use FieldTicket</span>
          <div className="flex items-center gap-2">
            <label className="flex items-center text-xs">
              <input
                type="checkbox"
                checked={perms.UseFieldTicket}
                onChange={(e) =>
                  handlePermToggle("UseFieldTicket", e.target.checked)
                }
                className="mr-1"
              />
              Enable
            </label>
            {/* Init status derived from clients.UseFieldTicket — set to Y by FT init console */}
            <span
              className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                formData.UseFieldTicket === "Y"
                  ? "bg-green-700/40 text-green-300"
                  : "bg-gray-600/40 text-gray-400"
              }`}
            >
              {formData.UseFieldTicket === "Y" ? "Init: Cleared" : "Init: Pending"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/** -------------------------------------
 *  FEATURES TAB
 * ------------------------------------- */
function FeaturesTab({ formData, onToggle, onChange, theme }) {
  const toggles = [
    { name: "useBSW", label: "Use BSW?" },
    { name: "use24", label: "Use 24-Hour Format?" },
    { name: "useMaps", label: "Use Maps?" },
    { name: "useLinePressure", label: "Use Line Pressure?" },
    { name: "ShowWells", label: "Show Wells?" },
    { name: "UseWater", label: "Use Water?" },
    { name: "UseGas", label: "Use Gas?" },
    { name: "ShowWellPropertyNum", label: "Show Well Property Num?" },
    { name: "UseWaterTanks", label: "Use Water Tanks?" },
    { name: "ShowZeros", label: "Show Zero Gauges?" },
    { name: "UseColorCut", label: "Use Color Cut?" },
    { name: "GaugeImportFile", label: "Gauge Import File?" },
    { name: "NetInterestBbls", label: "Net Interest Bbls?" },
    { name: "PR", label: "Use PR?" },
    { name: "ShowPOC", label: "Show POC?" },
    { name: "ShowLoads", label: "Show Loads?" },
    { name: "Data", label: "Use Data?" },
    { name: "RequireWellReason", label: "Require Well Reason?" },
    { name: "UseImages", label: "Use Images?" },
    { name: "UseFieldTicket", label: "Use Field Ticket?" },
    { name: "UseBradenheadPressure", label: "Use Bradenhead Pressure?" },
    { name: "EnableH10SelfInit", label: "Enable H-10 Self-Init?" },
    { name: "AlwaysOverrideRT", label: "Always Override RT?" },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {toggles.map((t) => (
        <div key={t.name} className="flex items-center space-x-2">
          <input
            id={t.name}
            type="checkbox"
            name={t.name}
            checked={formData[t.name] === "Y"}
            onChange={onToggle}
          />
          <label htmlFor={t.name} className="font-semibold">
            {t.label}
          </label>
        </div>
      ))}
      {/* PumperEditDaysBack */}
      <div>
        <label className="block mb-1 font-semibold">
          Pumper Edit Days Back
        </label>
        <input
          type="number"
          name="PumperEditDaysBack"
          value={formData.PumperEditDaysBack}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
    </div>
  );
}

/** -------------------------------------
 *  REGULATORY TAB
 *  Central place for the regulatory settings Lori/admin maintain:
 *  OperatorNum, PR (Production Reporting), PRStartDay, EnableH10SelfInit,
 *  plus an advisory about PRFiler (tenant-DB table — see notes inline).
 *  All four editable fields share formData with the rest of the modal, so
 *  the existing diff-aware PATCH (handleUpdate) saves them automatically
 *  via the master $masterCols whitelist in api/clientDetails.php.
 * ------------------------------------- */
function RegulatoryTab({ formData, onChange, onToggle, onPrFilerChange, theme, subdomain }) {
  const operatorNumLen = (formData.OperatorNum || "").length;
  const operatorNumBad = operatorNumLen > 6;
  const pf = formData.prFiler || { AgentID: "", AgentName: "", AgentEmail: "", AgentPhone: "" };
  const pfBad = {
    AgentID:    (pf.AgentID    || "").length > 9,
    AgentName:  (pf.AgentName  || "").length > 50,
    AgentEmail: (pf.AgentEmail || "").length > 50,
    AgentPhone: (pf.AgentPhone || "").length > 12,
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* OperatorNum */}
      <div>
        <label className="block mb-1 font-semibold">Operator Number</label>
        <input
          name="OperatorNum"
          value={formData.OperatorNum || ""}
          onChange={onChange}
          maxLength={6}
          placeholder="6-digit RRC operator (P-5)"
          className={`${getInputClasses(theme)}${operatorNumBad ? " border-red-500 ring-1 ring-red-500" : ""}`}
        />
        <p className="text-xs opacity-70 mt-1">
          Texas RRC P-5 operator number — max 6 characters. Used by the H-10 / PR EDI filings.
        </p>
        {operatorNumBad ? (
          <p className="text-xs text-red-600 mt-1">Operator Number must be 6 characters or fewer.</p>
        ) : null}
      </div>

      {/* PRStartDay */}
      <div>
        <label className="block mb-1 font-semibold">PR Reporting Cycle</label>
        <select
          name="PRStartDay"
          value={String(formData.PRStartDay ?? 2)}
          onChange={onChange}
          className={getInputClasses(theme)}
        >
          <option value="1">1 — 1st – End of Month</option>
          <option value="2">2 — 2nd – 1st</option>
        </select>
        <p className="text-xs opacity-70 mt-1">
          Calendar window the Production Report covers.
        </p>
      </div>

      {/* PR */}
      <div className="flex items-start space-x-2">
        <input
          id="reg_PR"
          type="checkbox"
          name="PR"
          checked={formData.PR === "Y"}
          onChange={onToggle}
          className="mt-1"
        />
        <label htmlFor="reg_PR" className="font-semibold flex-1">
          Production Reporting Enabled
          <span className="block text-xs font-normal opacity-70">
            Gates the PR / monthly Production Report module in the tenant app.
          </span>
        </label>
      </div>

      {/* EnableH10SelfInit */}
      <div className="flex items-start space-x-2">
        <input
          id="reg_EnableH10SelfInit"
          type="checkbox"
          name="EnableH10SelfInit"
          checked={formData.EnableH10SelfInit === "Y"}
          onChange={onToggle}
          className="mt-1"
        />
        <label htmlFor="reg_EnableH10SelfInit" className="font-semibold flex-1">
          Allow Company H-10 Self Init
          <span className="block text-xs font-normal opacity-70">
            Allows tenant users to submit simplified H-10 InjDispWellInfo setup
            from companyHome. Default OFF. Surfaces a gated tab at
            /companyHome/{subdomain || "<subdomain>"}.
          </span>
        </label>
      </div>

      {/* PR Filer — single-row tenant PRFilers editor (AgentID/Name/Email/Phone). */}
      <div className="md:col-span-2 mt-2">
        <div className={`rounded border p-3 ${theme === "dark" ? "border-gray-600" : "border-gray-300"}`}>
          <div className="font-semibold mb-2">PR Filer (RRC EDI filer)</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block mb-1 text-sm font-semibold">Agent ID</label>
              <input
                name="AgentID"
                value={pf.AgentID || ""}
                onChange={onPrFilerChange}
                maxLength={9}
                placeholder="e.g. 123456789"
                className={`${getInputClasses(theme)}${pfBad.AgentID ? " border-red-500 ring-1 ring-red-500" : ""}`}
              />
              <p className="text-xs opacity-60 mt-1">Up to 9 characters. RRC filer identifier.</p>
            </div>
            <div>
              <label className="block mb-1 text-sm font-semibold">Agent Name</label>
              <input
                name="AgentName"
                value={pf.AgentName || ""}
                onChange={onPrFilerChange}
                maxLength={50}
                placeholder="Filer / agent name"
                className={`${getInputClasses(theme)}${pfBad.AgentName ? " border-red-500 ring-1 ring-red-500" : ""}`}
              />
            </div>
            <div>
              <label className="block mb-1 text-sm font-semibold">Agent Email</label>
              <input
                type="email"
                name="AgentEmail"
                value={pf.AgentEmail || ""}
                onChange={onPrFilerChange}
                maxLength={50}
                placeholder="filer@example.com"
                className={`${getInputClasses(theme)}${pfBad.AgentEmail ? " border-red-500 ring-1 ring-red-500" : ""}`}
              />
            </div>
            <div>
              <label className="block mb-1 text-sm font-semibold">Agent Phone</label>
              <input
                name="AgentPhone"
                value={pf.AgentPhone || ""}
                onChange={onPrFilerChange}
                maxLength={12}
                placeholder="325-555-0100"
                className={`${getInputClasses(theme)}${pfBad.AgentPhone ? " border-red-500 ring-1 ring-red-500" : ""}`}
              />
              <p className="text-xs opacity-60 mt-1">Up to 12 characters.</p>
            </div>
          </div>
          <p className="text-xs opacity-70 mt-2">
            Saves into the tenant <strong>PRFilers</strong> row used by H-10 and PR EDI filings.
            Requires the H-10 module to be initialized for this tenant.
          </p>
        </div>
      </div>
    </div>
  );
}

/** -------------------------------------
 *  BILLING TAB
 * ------------------------------------- */
function BillingTab({ formData, onChange, onToggle, theme }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Bill */}
      <div className="flex items-center space-x-2">
        <input
          id="billToggle"
          type="checkbox"
          name="Bill"
          checked={formData.Bill === "Y"}
          onChange={onToggle}
        />
        <label htmlFor="billToggle" className="font-semibold">
          Bill?
        </label>
      </div>
      {/* BillQuarterly */}
      <div className="flex items-center space-x-2">
        <input
          id="billQuarterly"
          type="checkbox"
          name="BillQuarterly"
          checked={formData.BillQuarterly === "Y"}
          onChange={onToggle}
        />
        <label htmlFor="billQuarterly" className="font-semibold">
          Bill Quarterly?
        </label>
      </div>
      {/* Rate1 */}
      <div>
        <label className="block mb-1 font-semibold">Rate1</label>
        <input
          type="number"
          step="0.01"
          name="Rate1"
          value={formData.Rate1}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      {/* Rate2 */}
      <div>
        <label className="block mb-1 font-semibold">Rate2</label>
        <input
          type="number"
          step="0.01"
          name="Rate2"
          value={formData.Rate2}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      {/* Rate3 */}
      <div>
        <label className="block mb-1 font-semibold">Rate3</label>
        <input
          type="number"
          step="0.01"
          name="Rate3"
          value={formData.Rate3}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
    </div>
  );
}

/** -------------------------------------
 *  LAYOUT TAB
 * ------------------------------------- */
function LayoutTab({ formData, onChange, theme }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block mb-1 font-semibold">App Name</label>
        <input
          name="AppName"
          value={formData.AppName}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      <div>
        <label className="block mb-1 font-semibold">App Logo URL</label>
        <input
          name="AppLogo"
          value={formData.AppLogo}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      <div>
        <label className="block mb-1 font-semibold">App Author</label>
        <input
          name="AppAuthor"
          value={formData.AppAuthor}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      <div>
        <label className="block mb-1 font-semibold">Current Layout</label>
        <select
          name="CurrentLayout"
          value={formData.CurrentLayout}
          onChange={onChange}
          className={getInputClasses(theme)}
        >
          <option value="O">Original</option>
          <option value="A">Oct2023</option>
        </select>
      </div>
      <div>
        <label className="block mb-1 font-semibold">Color Cut Label</label>
        <input
          name="ColorCutLabel"
          value={formData.ColorCutLabel}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      <div>
        <label className="block mb-1 font-semibold">Current Custom Col</label>
        <input
          name="CurrentCustomCol"
          value={formData.CurrentCustomCol}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      <div>
        <label className="block mb-1 font-semibold">Current Custom Col2</label>
        <input
          name="CurrentCustomCol2"
          value={formData.CurrentCustomCol2}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      <div>
        <label className="block mb-1 font-semibold">Inj Custom Col1</label>
        <input
          name="InjCustomCol1"
          value={formData.InjCustomCol1}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      <div>
        <label className="block mb-1 font-semibold">Inj Custom Col2</label>
        <input
          name="InjCustomCol2"
          value={formData.InjCustomCol2}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
    </div>
  );
}

/** -------------------------------------
 *  SUPPORT TAB
 * ------------------------------------- */
function SupportTab({ formData, onChange, theme }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block mb-1 font-semibold">Support Email</label>
        <input
          name="SupportEmail"
          value={formData.SupportEmail}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      <div>
        <label className="block mb-1 font-semibold">Support Phone</label>
        <input
          name="SupportPhone"
          value={formData.SupportPhone}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
      <div className="md:col-span-2">
        <label className="block mb-1 font-semibold">Support Address</label>
        <input
          name="SupportAddress"
          value={formData.SupportAddress}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
    </div>
  );
}

/** -------------------------------------
 *  MISC TAB
 * ------------------------------------- */
function MiscTab({ formData, onChange, onToggle, theme }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block mb-1 font-semibold">Operator Num</label>
        <input
          name="OperatorNum"
          value={formData.OperatorNum}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>

      <div>
        <label className="block mb-1 font-semibold">PR Start Day</label>
        <input
          type="number"
          name="PRStartDay"
          value={formData.PRStartDay}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>

      <div>
        <label className="block mb-1 font-semibold">Manual Water Label</label>
        <input
          name="ManualWaterLabel"
          value={formData.ManualWaterLabel}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>

      <div>
        <label className="block mb-1 font-semibold">Manual Water Label 2</label>
        <input
          name="ManualWaterLabel2"
          value={formData.ManualWaterLabel2}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>

      <div>
        <label className="block mb-1 font-semibold">Line Height Inches</label>
        <input
          type="number"
          step="0.01"
          min="0"
          name="lineheightinches"
          value={formData.lineheightinches ?? ""}
          onChange={onChange}
          className={getInputClasses(theme)}
          placeholder="0.00"
        />
      </div>

      <div className="md:col-span-2">
        <label className="block mb-1 font-semibold">Notify Emails</label>
        <textarea
          name="NotifyEmails"
          rows={3}
          value={formData.NotifyEmails}
          onChange={onChange}
          className={getInputClasses(theme)}
        />
      </div>
    </div>
  );
}
export default CompanyDashboard;
